import { useMemo } from 'react'

import type { InfiniteData } from '@tanstack/react-query'

import { useSuggestions } from '@/extensions/Mention/hooks'
import type { Suggestions } from '@/extensions/Mention/types'
import {
  MentionList,
  type UserWithType,
} from '@/extensions/Mention/user/MentionList'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'

import type { PaginatedResponse } from '@/types/Api'
import type { User } from '@/types/User'

const getLabel = (user: UserWithType) => {
  return `${user.first_name} ${user.last_name}`
}

interface UseUserSuggestionsProps {
  onSelect?: (user: UserWithType) => void
}

const selectOrganizationUsers = (
  data: InfiniteData<PaginatedResponse<User>>,
): UserWithType[] => {
  return data.pages
    .map((res) => res.data)
    .flat()
    .sort((a, b) => a.first_name.localeCompare(b.first_name))
    .map((user) => ({
      ...user,
      mentionType: 'user' as const,
    }))
}

export const useUserSuggestions = ({
  onSelect,
}: UseUserSuggestionsProps): Suggestions | undefined => {
  const { activeOrgId } = useActiveOrgId()
  const { data: users } = useOrganizationUsers<UserWithType[]>({
    orgId: activeOrgId,
    select: selectOrganizationUsers,
  })
  return useSuggestions({
    component: MentionList,
    items: users ?? [],
    getLabel,
    onSelect,
  })
}
